import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Calendar, CreditCard, Loader2, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { getSupabaseFunctionErrorMessage } from '@/lib/supabase-function-error';

type SubscriptionAccess = {
  hasAccess: boolean;
  isTrial: boolean;
  trialEnd: string | null;
  trialExpired: boolean;
  isFounder: boolean;
  trialDays: number;
};

const EXEMPT_PATHS = ['/integrations', '/settings'];

function readableError(value: unknown, fallback: string): string {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (value && typeof value === 'object') {
    const record = value as { message?: unknown; error?: unknown; details?: unknown };
    if (typeof record.message === 'string' && record.message.trim()) return record.message.trim();
    if (typeof record.error === 'string' && record.error.trim()) return record.error.trim();
    if (typeof record.details === 'string' && record.details.trim()) return record.details.trim();
    try {
      return JSON.stringify(value);
    } catch {
      return fallback;
    }
  }
  return fallback;
}

async function fetchSubscriptionAccess(_userId: string): Promise<SubscriptionAccess> {
  const { data: sub, error: subError } = await supabase.functions.invoke('check-subscription');

  if (subError) {
    console.warn('[HotelSubscriptionGate] check-subscription failed:', subError.message);
  }

  const subData = sub as {
    subscribed?: boolean;
    is_trial?: boolean;
    trial_end?: string | null;
    trial_expired?: boolean;
    trial_days?: number;
    is_team?: boolean;
    is_founder?: boolean;
  } | null;

  const isFounder = subData?.is_founder === true || subData?.is_team === true;
  const trialActive = subData?.is_trial === true && subData?.trial_expired !== true;

  return {
    hasAccess: isFounder || subData?.subscribed === true || trialActive,
    isTrial: subData?.is_trial === true,
    trialEnd: subData?.trial_end ?? null,
    trialExpired: subData?.trial_expired === true,
    isFounder,
    trialDays: subData?.trial_days ?? 14,
  };
}

function TrialBanner({ trialEnd }: { trialEnd: string | null }) {
  if (!trialEnd) return null;
  return (
    <div className="border-b border-amber-200/80 bg-amber-50 px-4 py-2 text-center text-sm text-amber-950">
      <span className="font-medium">Free trial active</span>
      {' · '}
      ends {format(new Date(trialEnd), 'MMM d, yyyy')}
      {' · '}
      <Link to="/integrations" className="underline font-medium">
        Add billing before trial ends
      </Link>
    </div>
  );
}

function SubscriptionBlockedScreen({
  trialExpired,
  trialDays,
  onPrimaryAction,
  loading,
}: {
  trialExpired: boolean;
  trialDays: number;
  onPrimaryAction: () => void;
  loading: boolean;
}) {
  return (
    <div className="flex min-h-[70vh] items-center justify-center p-6">
      <Card className="max-w-lg w-full border-vesta-navy/10 shadow-lg">
        <CardHeader className="text-center space-y-3">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-vesta-gold/15">
            {trialExpired ? (
              <CreditCard className="h-6 w-6 text-vesta-navy" />
            ) : (
              <Sparkles className="h-6 w-6 text-vesta-gold" />
            )}
          </div>
          <CardTitle className="font-serif text-2xl text-vesta-navy">
            {trialExpired ? 'Your free trial has ended' : `Start your ${trialDays}-day free trial`}
          </CardTitle>
          <p className="text-sm text-vesta-navy/75 leading-relaxed">
            {trialExpired
              ? 'Subscribe to keep using Vesta CFO — dashboard, AI briefings, and integrations.'
              : `Full access for ${trialDays} days. No credit card required to start. After the trial, add billing to continue.`}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {!trialExpired && (
            <div className="rounded-lg border border-vesta-navy/10 bg-vesta-mist/30 px-4 py-3 text-sm text-vesta-navy/85">
              <div className="flex items-center gap-2 font-medium">
                <Calendar className="h-4 w-4" />
                $0 due today · then $149/mo after trial
              </div>
            </div>
          )}
          <Button
            className="w-full bg-vesta-navy hover:bg-vesta-navy/90"
            size="lg"
            onClick={onPrimaryAction}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {trialExpired ? 'Opening checkout...' : 'Starting trial...'}
              </>
            ) : trialExpired ? (
              'Subscribe — $149/mo'
            ) : (
              `Start ${trialDays}-day free trial`
            )}
          </Button>
          <p className="text-center text-xs text-vesta-navy/60">
            Questions?{' '}
            <a href="mailto:vestacfo@gmail.com" className="underline">
              vestacfo@gmail.com
            </a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export function HotelSubscriptionGate({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [primaryActionLoading, setPrimaryActionLoading] = useState(false);

  const isExempt = EXEMPT_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));

  const { data: access, isLoading } = useQuery({
    queryKey: ['hotel-subscription-access', user?.id],
    queryFn: () => fetchSubscriptionAccess(user!.id),
    enabled: !!user && !isExempt,
    staleTime: 60_000,
  });

  const handleOpenCheckout = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('create-hotel-checkout', { body: {} });
      if (error) {
        toast.error(await getSupabaseFunctionErrorMessage(error, 'Could not start checkout'));
        return;
      }
      const body = data as { url?: string; error?: string; already_subscribed?: boolean } | null;
      if (body?.already_subscribed) {
        toast.info('You already have an active subscription.');
        await queryClient.invalidateQueries({ queryKey: ['hotel-subscription-access'] });
        return;
      }
      if (body?.error) {
        toast.error(readableError(body.error, 'Could not start checkout'));
        return;
      }
      if (body?.url) {
        window.location.href = body.url;
        return;
      }
      toast.error('Could not start checkout');
    } catch (error) {
      console.error('[HotelSubscriptionGate] Checkout failed:', error);
      toast.error('Could not start checkout');
    }
  };

  const handlePrimaryAction = async () => {
    setPrimaryActionLoading(true);
    try {
      if (access?.trialExpired) {
        await handleOpenCheckout();
        return;
      }

      const { data, error } = await supabase.functions.invoke('start-hotel-trial', { body: {} });
      if (error) {
        toast.error(await getSupabaseFunctionErrorMessage(error, 'Could not start free trial'));
        return;
      }

      const body = data as {
        success?: boolean;
        trial_end?: string;
        trial_expired?: boolean;
        error?: unknown;
      } | null;

      if (body?.error) {
        toast.error(readableError(body.error, 'Could not start free trial'));
        return;
      }

      if (body?.trial_expired) {
        toast.info('Your free trial has ended. Add billing to continue.');
        await handleOpenCheckout();
        return;
      }

      if (body?.success) {
        toast.success('Free trial started. You have full access for 14 days.');
        await queryClient.invalidateQueries({ queryKey: ['hotel-subscription-access'] });
        return;
      }

      toast.error('Could not start free trial');
    } finally {
      setPrimaryActionLoading(false);
    }
  };

  if (isExempt) {
    return <>{children}</>;
  }

  if (isLoading || !access) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-vesta-gold" />
      </div>
    );
  }

  if (!access.hasAccess) {
    return (
      <SubscriptionBlockedScreen
        trialExpired={access.trialExpired}
        trialDays={access.trialDays}
        onPrimaryAction={handlePrimaryAction}
        loading={primaryActionLoading}
      />
    );
  }

  return (
    <>
      {access.isTrial && <TrialBanner trialEnd={access.trialEnd} />}
      {access.isFounder && (
        <div className="border-b border-emerald-200/80 bg-emerald-50 px-4 py-1.5 text-center text-xs text-emerald-900">
          <Badge variant="outline" className="border-emerald-300 bg-white/60 text-emerald-900">
            Founder access
          </Badge>
        </div>
      )}
      {children}
    </>
  );
}

export default HotelSubscriptionGate;
